/**
 * Focused, single-purpose modals for the org-chart quick-action menus.
 *
 * Deliberately granular (one job each) so the chart's ⋮ menus can map each
 * menu item to a small, obvious dialog rather than the everything-at-once
 * `UnitEditorModal` used by the /hr/org-structure admin table. Each modal
 * owns its own busy/error UI but takes an `onSave` closure so the caller
 * wires it to the right mutation + query invalidation.
 */
import { useEffect, useMemo, useState } from 'react'
import type { OrgUnitType } from '@lms/shared-types'
import { Modal } from '../../../primitives/Modal'
import { Button } from '../../../primitives/Button'
import { Combobox } from '../../../primitives/Combobox'
import { UnitTypeChip } from '../UnitTypeChip'
import type { EmpLite, UnitLite } from './OrgStructureEditors'

/** What a box can contain. Units are `org_units` rows; `team` is a `teams`
 *  row parented at the unit. */
export type ChildKind = OrgUnitType | 'team'

const CHILD_LABEL: Record<ChildKind, string> = {
  company: 'Company',
  function: 'Function',
  department: 'Department',
  team: 'Team',
}

const fieldStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid var(--border)',
  borderRadius: 8,
  fontSize: 14,
  background: 'var(--surface)',
  color: 'var(--text)',
}
const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 12,
  color: 'var(--text-2)',
}

function errText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback
}

// ── Rename (generic: unit or team) ──────────────────────────────────────────

interface RenameModalProps {
  open: boolean
  onClose: () => void
  /** e.g. "Rename team" */
  title: string
  /** field label, e.g. "Team name" */
  label: string
  currentName: string
  onSave: (name: string) => Promise<void>
}

export function RenameModal({
  open,
  onClose,
  title,
  label,
  currentName,
  onSave,
}: RenameModalProps): JSX.Element {
  const [name, setName] = useState(currentName)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setName(currentName)
      setError(null)
      setBusy(false)
    }
  }, [open, currentName])

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    const trimmed = name.trim()
    if (trimmed.length === 0) {
      setError('Name is required.')
      return
    }
    if (trimmed === currentName) {
      onClose()
      return
    }
    setBusy(true)
    setError(null)
    try {
      await onSave(trimmed)
      onClose()
    } catch (err) {
      setError(errText(err, 'Could not rename.'))
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="org-rename-form"
            loading={busy}
            data-testid="org-rename-save"
          >
            Save
          </Button>
        </>
      }
    >
      <form
        id="org-rename-form"
        onSubmit={handleSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <label style={labelStyle}>
          <span>{label}</span>
          <input
            data-testid="org-rename-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            maxLength={120}
            style={fieldStyle}
          />
        </label>
        {error ? (
          <p
            data-testid="org-rename-error"
            style={{ fontSize: 13, color: 'var(--danger-fg)', margin: 0 }}
          >
            {error}
          </p>
        ) : null}
      </form>
    </Modal>
  )
}

// ── Set / change a unit head ────────────────────────────────────────────────

interface UnitHeadModalProps {
  open: boolean
  onClose: () => void
  unitName: string
  currentHeadId: string | null
  employees: EmpLite[]
  /** null clears the head. */
  onSave: (headId: string | null) => Promise<void>
}

export function UnitHeadModal({
  open,
  onClose,
  unitName,
  currentHeadId,
  employees,
  onSave,
}: UnitHeadModalProps): JSX.Element {
  const [headId, setHeadId] = useState(currentHeadId ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setHeadId(currentHeadId ?? '')
      setError(null)
      setBusy(false)
    }
  }, [open, currentHeadId])

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    const next = headId || null
    if (next === (currentHeadId ?? null)) {
      onClose()
      return
    }
    setBusy(true)
    setError(null)
    try {
      await onSave(next)
      onClose()
    } catch (err) {
      setError(errText(err, 'Could not update the head.'))
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Head of “${unitName}”`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="org-head-form"
            loading={busy}
            data-testid="org-head-save"
          >
            Save
          </Button>
        </>
      }
    >
      <form
        id="org-head-form"
        onSubmit={handleSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <label style={labelStyle}>
          <span>Head</span>
          <Combobox
            inputTestId="org-head-input"
            ariaLabel="Head"
            placeholder="Search an employee…"
            emptyOptionLabel="— no head —"
            value={headId}
            onChange={setHeadId}
            options={employees.map((emp) => ({
              value: emp.id,
              label: emp.name,
              sublabel: emp.jobTitle,
            }))}
          />
        </label>
        {error ? (
          <p
            data-testid="org-head-error"
            style={{ fontSize: 13, color: 'var(--danger-fg)', margin: 0 }}
          >
            {error}
          </p>
        ) : null}
      </form>
    </Modal>
  )
}

// ── Move a unit to a new parent (or detach to a root) ───────────────────────

interface UnitParentModalProps {
  open: boolean
  onClose: () => void
  unitName: string
  currentParentId: string | null
  /** Candidate parent units. The caller has already dropped the unit itself
   *  and all its descendants, so nothing here can form a cycle. */
  units: UnitLite[]
  onSave: (parentId: string | null) => Promise<void>
}

export function UnitParentModal({
  open,
  onClose,
  unitName,
  currentParentId,
  units,
  onSave,
}: UnitParentModalProps): JSX.Element {
  const [parentId, setParentId] = useState(currentParentId ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setParentId(currentParentId ?? '')
      setError(null)
      setBusy(false)
    }
  }, [open, currentParentId])

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    const next = parentId || null
    if (next === (currentParentId ?? null)) {
      onClose()
      return
    }
    setBusy(true)
    setError(null)
    try {
      await onSave(next)
      onClose()
    } catch (err) {
      setError(errText(err, 'Could not move the unit.'))
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Move “${unitName}”`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="org-unit-parent-form"
            loading={busy}
            data-testid="org-unit-parent-save"
          >
            Save
          </Button>
        </>
      }
    >
      <form
        id="org-unit-parent-form"
        onSubmit={handleSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <label style={labelStyle}>
          <span>Parent unit</span>
          <Combobox
            inputTestId="org-unit-parent-input"
            ariaLabel="Parent unit"
            placeholder="Search a unit…"
            emptyOptionLabel="— none (top level) —"
            value={parentId}
            onChange={setParentId}
            options={units.map((u) => ({
              value: u.id,
              label: u.name,
              badge: <UnitTypeChip unitType={u.unitType} />,
            }))}
          />
        </label>
        {error ? (
          <p
            data-testid="org-unit-parent-error"
            style={{ fontSize: 13, color: 'var(--danger-fg)', margin: 0 }}
          >
            {error}
          </p>
        ) : null}
      </form>
    </Modal>
  )
}

// ── Change a unit's type (company / function / department) ──────────────────

const UNIT_TYPE_OPTIONS: { value: OrgUnitType; label: string }[] = [
  { value: 'company', label: 'Company' },
  { value: 'function', label: 'Function' },
  { value: 'department', label: 'Department' },
]

interface UnitTypeModalProps {
  open: boolean
  onClose: () => void
  unitName: string
  currentType: OrgUnitType
  onSave: (unitType: OrgUnitType) => Promise<void>
}

export function UnitTypeModal({
  open,
  onClose,
  unitName,
  currentType,
  onSave,
}: UnitTypeModalProps): JSX.Element {
  const [unitType, setUnitType] = useState<OrgUnitType>(currentType)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setUnitType(currentType)
      setError(null)
      setBusy(false)
    }
  }, [open, currentType])

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    if (unitType === currentType) {
      onClose()
      return
    }
    setBusy(true)
    setError(null)
    try {
      await onSave(unitType)
      onClose()
    } catch (err) {
      setError(errText(err, 'Could not change the type.'))
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Type of “${unitName}”`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="org-unit-type-form"
            loading={busy}
            data-testid="org-unit-type-save"
          >
            Save
          </Button>
        </>
      }
    >
      <form
        id="org-unit-type-form"
        onSubmit={handleSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <label style={labelStyle}>
          <span>Type</span>
          <select
            data-testid="org-unit-type-select"
            value={unitType}
            onChange={(e) => setUnitType(e.target.value as OrgUnitType)}
            style={fieldStyle}
          >
            {UNIT_TYPE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        {error ? (
          <p
            data-testid="org-unit-type-error"
            style={{ fontSize: 13, color: 'var(--danger-fg)', margin: 0 }}
          >
            {error}
          </p>
        ) : null}
      </form>
    </Modal>
  )
}

// ── Add a child (function / department / team) under a unit ──────────────────

interface AddChildModalProps {
  open: boolean
  onClose: () => void
  parentName: string
  /** Child kinds this parent may contain, per the org hierarchy. Empty =
   *  modal renders nothing meaningful (guarded by the caller). */
  allowedKinds: ChildKind[]
  employees: EmpLite[]
  /** Create a function/department under the parent. */
  onCreateUnit: (kind: OrgUnitType, name: string, headId: string | null) => Promise<void>
  /** Create a team under the parent. */
  onCreateTeam: (name: string) => Promise<void>
}

export function AddChildModal({
  open,
  onClose,
  parentName,
  allowedKinds,
  employees,
  onCreateUnit,
  onCreateTeam,
}: AddChildModalProps): JSX.Element {
  const [kind, setKind] = useState<ChildKind>(allowedKinds[0] ?? 'team')
  const [name, setName] = useState('')
  const [headId, setHeadId] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setKind(allowedKinds[0] ?? 'team')
      setName('')
      setHeadId('')
      setError(null)
      setBusy(false)
    }
  }, [open, allowedKinds])

  const singleKind = allowedKinds.length === 1

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    const trimmed = name.trim()
    if (trimmed.length === 0) {
      setError(`${CHILD_LABEL[kind]} name is required.`)
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (kind === 'team') {
        await onCreateTeam(trimmed)
      } else {
        await onCreateUnit(kind, trimmed, headId || null)
      }
      onClose()
    } catch (err) {
      setError(errText(err, `Could not add ${CHILD_LABEL[kind].toLowerCase()}.`))
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        singleKind
          ? `Add ${CHILD_LABEL[allowedKinds[0] ?? 'team'].toLowerCase()} to “${parentName}”`
          : `Add to “${parentName}”`
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="org-addchild-form"
            loading={busy}
            data-testid="org-addchild-save"
          >
            Create
          </Button>
        </>
      }
    >
      <form
        id="org-addchild-form"
        onSubmit={handleSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        {singleKind ? null : (
          <label style={labelStyle}>
            <span>Type</span>
            <select
              data-testid="org-addchild-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as ChildKind)}
              style={fieldStyle}
            >
              {allowedKinds.map((k) => (
                <option key={k} value={k}>
                  {CHILD_LABEL[k]}
                </option>
              ))}
            </select>
          </label>
        )}

        <label style={labelStyle}>
          <span>{CHILD_LABEL[kind]} name</span>
          <input
            data-testid="org-addchild-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            maxLength={120}
            style={fieldStyle}
          />
        </label>

        {kind === 'team' ? null : (
          <label style={labelStyle}>
            <span>Head (optional)</span>
            <Combobox
              inputTestId="org-addchild-head"
              ariaLabel="Head"
              placeholder="Search an employee…"
              emptyOptionLabel="— no head —"
              value={headId}
              onChange={setHeadId}
              options={employees.map((emp) => ({
                value: emp.id,
                label: emp.name,
                sublabel: emp.jobTitle,
              }))}
            />
          </label>
        )}

        {error ? (
          <p
            data-testid="org-addchild-error"
            style={{ fontSize: 13, color: 'var(--danger-fg)', margin: 0 }}
          >
            {error}
          </p>
        ) : null}
      </form>
    </Modal>
  )
}

// ── Add existing employees to a team ────────────────────────────────────────

interface AddMembersModalProps {
  open: boolean
  onClose: () => void
  teamName: string
  /** Employees not already in the team. */
  candidates: EmpLite[]
  onAdd: (employeeIds: string[]) => Promise<void>
}

export function AddMembersModal({
  open,
  onClose,
  teamName,
  candidates,
  onAdd,
}: AddMembersModalProps): JSX.Element {
  const [staged, setStaged] = useState<string[]>([])
  const [pick, setPick] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setStaged([])
      setPick('')
      setError(null)
      setBusy(false)
    }
  }, [open])

  const byId = useMemo(
    () => new Map(candidates.map((c) => [c.id, c])),
    [candidates],
  )
  // Options exclude anyone already staged so you can't add a person twice.
  const options = useMemo(
    () => candidates.filter((c) => !staged.includes(c.id)),
    [candidates, staged],
  )

  function addPick(id: string): void {
    if (!id) return
    setStaged((s) => (s.includes(id) ? s : [...s, id]))
    setPick('')
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    if (staged.length === 0) {
      setError('Pick at least one person to add.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await onAdd(staged)
      onClose()
    } catch (err) {
      setError(errText(err, 'Could not add members.'))
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Add members to “${teamName}”`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="org-addmembers-form"
            loading={busy}
            disabled={busy || staged.length === 0}
            data-testid="org-addmembers-save"
          >
            {staged.length > 0 ? `Add ${staged.length}` : 'Add'}
          </Button>
        </>
      }
    >
      <form
        id="org-addmembers-form"
        onSubmit={handleSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <label style={labelStyle}>
          <span>Add people</span>
          <Combobox
            inputTestId="org-addmembers-pick"
            ariaLabel="Add people"
            placeholder="Search an employee…"
            value={pick}
            onChange={addPick}
            options={options.map((emp) => ({
              value: emp.id,
              label: emp.name,
              sublabel: emp.jobTitle,
            }))}
          />
        </label>

        {staged.length > 0 ? (
          <div
            data-testid="org-addmembers-staged"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
          >
            {staged.map((id) => (
              <span
                key={id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 8px',
                  borderRadius: 999,
                  fontSize: 12,
                  background: 'var(--primary-bg, rgba(59,130,246,0.12))',
                  color: 'var(--text)',
                }}
              >
                {byId.get(id)?.name ?? id}
                <button
                  type="button"
                  aria-label={`Remove ${byId.get(id)?.name ?? 'person'}`}
                  onClick={() => setStaged((s) => s.filter((x) => x !== id))}
                  style={{
                    appearance: 'none',
                    border: 0,
                    background: 'transparent',
                    color: 'var(--text-3)',
                    cursor: 'pointer',
                    fontSize: 14,
                    lineHeight: 1,
                    padding: 0,
                  }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p style={{ fontSize: 12, color: 'var(--text-3)', margin: 0 }}>
            Pick people to move into this team.
          </p>
        )}

        {error ? (
          <p
            data-testid="org-addmembers-error"
            style={{ fontSize: 13, color: 'var(--danger-fg)', margin: 0 }}
          >
            {error}
          </p>
        ) : null}
      </form>
    </Modal>
  )
}
